import type React from "react";

import { useDraggable } from "@dnd-kit/core";
import { Stack, Typography } from "@mui/material";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { UserCourseLesson } from "api/schemas/UserCourse";
import type { ExerciseListItem } from "api/services/content/schemas/exercises";
import { BaseButton } from "shared/atoms/BaseButton";
import { DARK_BLUE } from "theme/colors";

import { ActivityImage, DragHandle } from "../shared";
import type { ActivityType, HandleAddActivity } from "../types";

import { normalizeActivity } from "./utils";

interface ActivityLibraryItemProps {
  activity: ExerciseListItem | UserCourseLesson;
  isAdded?: boolean;
  hideControls?: boolean;
  type: ActivityType;
  onAddActivity?: HandleAddActivity;
  onPreview?: (id: number) => void;
  onRemoveActivity?: (id: number, type: ActivityType) => void;
}

export const ActivityLibraryItem: React.FC<ActivityLibraryItemProps> = ({
  activity,
  isAdded,
  hideControls = false,
  type,
  onAddActivity,
  onPreview,
  onRemoveActivity,
}) => {
  const { t } = useTranslation();

  const normalizedActivity = normalizeActivity(activity);

  const { attributes, isDragging, listeners, setNodeRef } = useDraggable({
    id: `library-${type}-${normalizedActivity.id}`,
    data: {
      type: "library_item",
      activity,
      activityType: type,
    },
  });
  const isDragDisabled = isAdded && type === "lesson"; // Only disable dragging for lessons that are already added
  const dragHandleProps = isDragDisabled ? {} : { ...attributes, ...listeners };

  return (
    <Container
      ref={isDragDisabled ? null : setNodeRef}
      className={clsx({ added: isAdded, disabled: isDragDisabled, dragging: isDragging })}
      {...dragHandleProps}
    >
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
        {!hideControls && <DragHandle isDisabled={isAdded} />}

        <ActivityImage isAdded={isAdded} src={normalizedActivity.imageUrl} title={normalizedActivity.title} />

        <Stack spacing={0.5}>
          <Typography variant="subtitle2">{normalizedActivity.title}</Typography>
          <Typography variant="label" color={DARK_BLUE[70]}>
            {t("common.levels_icu", { count: normalizedActivity.levelCount })}
          </Typography>
        </Stack>
      </Stack>

      {!hideControls && (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <BaseButton
            variant="action"
            icon="eye"
            size="small"
            title={t("treatment.editor.preview_activity", { activityType: type })}
            onClick={event => {
              event.stopPropagation();
              onPreview?.(normalizedActivity.id);
            }}
          />
          {isAdded ? (
            <BaseButton
              variant="action"
              className="danger"
              icon="trash"
              size="small"
              title={t("treatment.editor.remove_activity", { activityType: type })}
              onClick={event => {
                event.stopPropagation();
                onRemoveActivity?.(normalizedActivity.id, type);
              }}
              style={{ flexBasis: "32px" }}
            />
          ) : (
            <BaseButton
              variant="primary"
              icon="plus"
              size="small"
              square
              title={t("treatment.editor.add_activity", { activityType: type })}
              onClick={event => {
                event.stopPropagation();
                onAddActivity?.(activity, type);
              }}
              style={{ flexBasis: "32px" }}
            />
          )}
        </Stack>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.redesign.db30};
  border-radius: 8px;
  transition: all 0.2s ease;
  cursor: grab;

  &:hover {
    background-color: ${({ theme }) => theme.colors.redesign.db1};
    border-color: ${({ theme }) => theme.colors.redesign.db40};
  }

  &:active {
    cursor: grabbing;
  }

  &.added {
    background-color: ${({ theme }) => theme.colors.redesign.g10};
    border-color: ${({ theme }) => theme.colors.redesign.g60};

    &:hover {
      border-color: ${({ theme }) => theme.colors.redesign.g100};
    }
  }

  &.disabled {
    cursor: not-allowed;
  }

  &.dragging {
    opacity: 0.5;
    transition: none;
  }
`;
